import * as React from 'react';
import { Alert, Box, Button, CircularProgress, Link, TextField, Typography } from '@mui/material';

import { useSignup } from '../context';
import { errorText } from '../labels';

// Verify e-mail (plan §8.3, D12): a link without a bound e-mail confirms the address the client types
// with a 6-digit code before create. Bound links never come here.

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function VerifyEmail() {
    const { api, flow, dispatch, sessionLost } = useSignup();
    const [email, setEmail] = React.useState(flow.verifiedEmail || '');
    const [sentTo, setSentTo] = React.useState<string | null>(null);
    const [expiresIn, setExpiresIn] = React.useState<number | null>(null);
    const [code, setCode] = React.useState('');
    const [busy, setBusy] = React.useState<'send' | 'verify' | null>(null);
    const [error, setError] = React.useState<string | null>(flow.flash ? flow.flash.message : null);
    const [emailProblem, setEmailProblem] = React.useState<string | null>(null);
    const mounted = React.useRef(true);
    React.useEffect(() => () => { mounted.current = false; }, []);

    const send = async (ev?: React.FormEvent) => {
        if (ev) { ev.preventDefault(); }
        const address = email.trim();
        if (!EMAIL_RE.test(address)) {
            setEmailProblem('Enter a valid e-mail address.');
            return;
        }
        setEmailProblem(null);
        setError(null);
        setBusy('send');
        try {
            const sent = await api.sendEmailCode(address);
            if (!mounted.current) { return; }
            setSentTo(address);
            setExpiresIn(sent.expires_in);
            setCode('');
        } catch (e) {
            if (sessionLost(e)) { return; }
            if (mounted.current) { setError(errorText(e)); }
        }
        if (mounted.current) { setBusy(null); }
    };

    const verify = async (ev: React.FormEvent) => {
        ev.preventDefault();
        if (!sentTo || !/^\d{6}$/.test(code)) {
            setError('Enter the 6-digit code from the e-mail.');
            return;
        }
        setError(null);
        setBusy('verify');
        try {
            const verified = await api.verifyEmail(sentTo, code);
            dispatch({ type: 'verified', email: verified.verified_email });
            return;
        } catch (e: any) {
            if (sessionLost(e)) { return; }
            if (!mounted.current) { return; }
            if (e && (e.code === 'too_many_attempts' || e.code === 'code_expired')) {
                // The code is spent: a new one is needed.
                setSentTo(null);
                setCode('');
            }
            let message = errorText(e);
            if (e && e.code === 'code_invalid' && e.attemptsLeft != null) {
                message = `${message} ${e.attemptsLeft} attempt${e.attemptsLeft === 1 ? '' : 's'} left.`;
            }
            setError(message);
        }
        if (mounted.current) { setBusy(null); }
    };

    return (
        <Box>
            <Typography variant="h5" component="h1" gutterBottom>Confirm your e-mail address</Typography>
            <Typography color="text.secondary" paragraph>
                You will sign in to your dashboard with this address. We send a code to it to make sure it is yours.
            </Typography>
            {!sentTo ? (
                <Box component="form" noValidate onSubmit={send} aria-label="Send a code">
                    <TextField
                        id="signup-email"
                        type="email"
                        label="E-mail address"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        fullWidth
                        margin="normal"
                        required
                        autoFocus
                        autoComplete="email"
                        inputProps={{ maxLength: 254 }}
                        error={!!emailProblem}
                        helperText={emailProblem || ' '}
                        disabled={!!busy}
                    />
                    {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
                    <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                        <Button type="button" onClick={() => dispatch({ type: 'go', step: 'confirm' })} disabled={!!busy}>Back</Button>
                        <Button type="submit" variant="contained" disabled={!!busy}
                            startIcon={busy === 'send' ? <CircularProgress size={18} color="inherit" /> : undefined}>
                            Send code
                        </Button>
                    </Box>
                </Box>
            ) : (
                <Box component="form" noValidate onSubmit={verify} aria-label="Enter the code">
                    <Typography paragraph>
                        We sent a 6-digit code to <strong>{sentTo}</strong>
                        {expiresIn ? `. It is valid for ${Math.round(expiresIn / 60)} minutes.` : '.'}
                    </Typography>
                    <TextField
                        id="signup-email-code"
                        label="Code"
                        value={code}
                        onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        margin="normal"
                        required
                        autoFocus
                        autoComplete="one-time-code"
                        inputProps={{ inputMode: 'numeric', maxLength: 6, pattern: '[0-9]*' }}
                        disabled={!!busy}
                    />
                    {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
                    <Box sx={{ mt: 2, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                        <Button type="submit" variant="contained" disabled={!!busy}
                            startIcon={busy === 'verify' ? <CircularProgress size={18} color="inherit" /> : undefined}>
                            Confirm
                        </Button>
                        <Link component="button" type="button" variant="body2" onClick={() => send()} disabled={!!busy}>
                            Send a new code
                        </Link>
                        <Link component="button" type="button" variant="body2" onClick={() => { setSentTo(null); setError(null); }} disabled={!!busy}>
                            Use another address
                        </Link>
                    </Box>
                </Box>
            )}
        </Box>
    );
}
